<script setup lang="ts">
/**
 * `/chat` — OpenAI-style playground that talks to the Hub session proxy.
 *
 * Layout: split.
 *   - Left rail (260px on lg+, collapses to top on mobile) — settings.
 *   - Right pane — message thread + composer.
 *
 * The view owns all chat state and orchestrates streaming:
 *   1. Loads `/v1/models` (router, public) and `/api/keys` (hub) on mount.
 *   2. On send: builds the OpenAI-style `messages[]` (system + history +
 *      new user), starts an SSE stream against the Hub session endpoint
 *      `POST /api/chat/completions` with the selected `keyId`. The Hub
 *      verifies ownership/active and forwards to the router via the
 *      trusted internal auth path; the SPA never sees the raw API key.
 *   3. `tool_calls` deltas are accumulated by `index`: streamed chunks
 *      with the same index merge `function.arguments` concat-style.
 *   4. Aborting (`Stop`) cancels the underlying fetch via AbortController.
 *
 * Error handling intentionally surfaces in TWO places:
 *   - top-level banner (no key, no model, models endpoint down) — at top
 *     of the thread, doesn't block the conversation history.
 *   - Inline on the failing assistant message — keeps cause adjacent
 *     to the user's prompt that triggered it.
 */
import { computed, onMounted, ref, watch } from 'vue';
import { useKeysStore } from '../stores/keys';
import ChatSettings from '../components/chat/ChatSettings.vue';
import ChatThread from '../components/chat/ChatThread.vue';
import type { ChatUiMessage } from '../components/chat/ChatThread.vue';
import ChatInput from '../components/chat/ChatInput.vue';
import {
  listModels,
  streamHubChatCompletion,
  ECHO_VALUE_TOOL,
  type ChatMessageParam,
  type OpenAIModel,
  type ToolCall,
} from '../lib/openai-client';

// ---------------------------------------------------------------------------
// Settings state (driven by ChatSettings via v-model).
// ---------------------------------------------------------------------------

const selectedModel = ref<string>('');
const selectedKeyId = ref<string | null>(null);
const temperature = ref(0.7);
const maxTokens = ref(1024);
const systemPrompt = ref('You are a helpful assistant.');
const toolCallingEnabled = ref(false);

// ---------------------------------------------------------------------------
// Live data: models from router, keys from hub.
// ---------------------------------------------------------------------------

const models = ref<OpenAIModel[]>([]);
const modelsLoading = ref(false);
const modelsError = ref<string | null>(null);

const keysStore = useKeysStore();
const activeKeys = computed(() => keysStore.items.filter((k) => !k.revoked_at));

async function refreshModels(): Promise<void> {
  modelsLoading.value = true;
  modelsError.value = null;
  try {
    models.value = await listModels();
    if (models.value.length > 0 && !selectedModel.value) {
      selectedModel.value = models.value[0].id;
    }
  } catch (err) {
    modelsError.value = (err as Error).message || 'Failed to load models';
  } finally {
    modelsLoading.value = false;
  }
}

onMounted(async () => {
  await Promise.all([
    refreshModels(),
    keysStore.items.length === 0 ? keysStore.load() : Promise.resolve(),
  ]);
});

// Auto-pick the first ACTIVE key once the keys list resolves.
watch(
  activeKeys,
  (next) => {
    if (selectedKeyId.value === null && next.length > 0) {
      selectedKeyId.value = next[0].id;
    } else if (selectedKeyId.value !== null && !next.some((k) => k.id === selectedKeyId.value)) {
      // Selected key got revoked — fall back to first active or null.
      selectedKeyId.value = next.length > 0 ? next[0].id : null;
    }
  },
  { immediate: true },
);

// ---------------------------------------------------------------------------
// Chat thread state.
// ---------------------------------------------------------------------------

const messages = ref<ChatUiMessage[]>([]);
const streaming = ref(false);
let abortController: AbortController | null = null;

function makeId(): string {
  return `m_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

const banner = computed<string | null>(() => {
  if (modelsError.value && models.value.length === 0) return modelsError.value;
  return null;
});

function clearThread(): void {
  if (streaming.value) stopStreaming();
  messages.value = [];
}

function stopStreaming(): void {
  if (abortController) {
    abortController.abort();
    abortController = null;
  }
  streaming.value = false;
  // Mark any still-streaming row as no longer in flight.
  for (const m of messages.value) {
    if (m.streaming) m.streaming = false;
  }
}

/**
 * Build the OpenAI-format messages array from our UI state. System prompt
 * (if any) goes first, followed by the conversation history. Each UI row
 * with `tool_calls` becomes an assistant message with `content: null` +
 * `tool_calls`, matching the canonical OpenAI shape exercised by
 * `test/unit/chat-tool-fields.test.ts`.
 */
function buildOpenAIMessages(): ChatMessageParam[] {
  const out: ChatMessageParam[] = [];
  const sys = systemPrompt.value.trim();
  if (sys.length > 0) out.push({ role: 'system', content: sys });

  for (const m of messages.value) {
    if (m.role === 'user') {
      out.push({ role: 'user', content: m.content });
    } else if (m.role === 'assistant') {
      const hasCalls = (m.tool_calls?.length ?? 0) > 0;
      if (hasCalls) {
        out.push({
          role: 'assistant',
          content: m.content.length > 0 ? m.content : null,
          tool_calls: m.tool_calls,
        });
      } else {
        out.push({ role: 'assistant', content: m.content });
      }
    } else if (m.role === 'tool') {
      out.push({
        role: 'tool',
        content: m.content,
        tool_call_id: m.tool_call_id ?? 'unknown',
      });
    }
  }
  return out;
}

/**
 * Merge an incoming streaming tool_call delta into the assistant row.
 * OpenAI streams tool calls by `index`: the first chunk carries `id`,
 * `type`, `function.name`; subsequent chunks at the same index carry
 * partial `function.arguments` to concatenate.
 */
function mergeToolCalls(target: ChatUiMessage, deltas: ToolCall[]): void {
  if (!target.tool_calls) target.tool_calls = [];
  for (const d of deltas) {
    const idx = typeof d.index === 'number' ? d.index : target.tool_calls.length;
    while (target.tool_calls.length <= idx) {
      target.tool_calls.push({ index: target.tool_calls.length, function: {} });
    }
    const existing = target.tool_calls[idx];
    if (d.id) existing.id = d.id;
    if (d.type) existing.type = d.type;
    if (d.function?.name) existing.function.name = d.function.name;
    if (typeof d.function?.arguments === 'string') {
      existing.function.arguments = (existing.function.arguments ?? '') + d.function.arguments;
    }
  }
}

async function onSend(text: string): Promise<void> {
  if (streaming.value) return;
  const keyId = selectedKeyId.value;
  const activeKey = activeKeys.value.find((k) => k.id === keyId);
  if (!activeKey) {
    messages.value = [
      ...messages.value,
      {
        id: makeId(),
        role: 'assistant',
        content: '',
        error: 'No API key selected. Create one on the Keys page.',
      },
    ];
    return;
  }
  if (!selectedModel.value) {
    messages.value = [
      ...messages.value,
      {
        id: makeId(),
        role: 'assistant',
        content: '',
        error: 'No model selected.',
      },
    ];
    return;
  }

  // Append the user message + a fresh assistant placeholder for streaming.
  const userMsg: ChatUiMessage = { id: makeId(), role: 'user', content: text };
  const assistantMsg: ChatUiMessage = {
    id: makeId(),
    role: 'assistant',
    content: '',
    streaming: true,
  };
  messages.value = [...messages.value, userMsg, assistantMsg];

  streaming.value = true;
  abortController = new AbortController();

  const body = {
    model: selectedModel.value,
    messages: buildOpenAIMessages(),
    temperature: temperature.value,
    max_tokens: maxTokens.value,
    ...(toolCallingEnabled.value
      ? { tools: [ECHO_VALUE_TOOL], tool_choice: 'auto' as const, parallel_tool_calls: false }
      : {}),
  };

  // Push the user prompt the model still needs to see — `buildOpenAIMessages`
  // already includes everything *up to* this point (the assistant placeholder
  // is empty, so it serializes as an empty string and the model can still
  // respond). To avoid sending the empty assistant turn, drop the last item.
  body.messages = body.messages.slice(0, body.messages.length - 1);

  await streamHubChatCompletion(
    activeKey.id,
    body,
    {
      onDelta: (delta) => {
        const target = messages.value.find((m) => m.id === assistantMsg.id);
        if (!target) return;
        if (delta.content) target.content += delta.content;
        if (delta.reasoning) target.reasoning = (target.reasoning ?? '') + delta.reasoning;
        if (delta.tool_calls) mergeToolCalls(target, delta.tool_calls);
      },
      onError: (err) => {
        const target = messages.value.find((m) => m.id === assistantMsg.id);
        if (target) {
          target.streaming = false;
          target.error = `${err.message} (HTTP ${err.status})`;
        }
        streaming.value = false;
        abortController = null;
      },
      onDone: () => {
        const target = messages.value.find((m) => m.id === assistantMsg.id);
        if (target) target.streaming = false;
        streaming.value = false;
        abortController = null;
      },
    },
    abortController.signal,
  );
}

</script>

<template>
  <div class="h-[calc(100vh-3.5rem)] grid grid-rows-[auto_1fr] lg:grid-rows-1 lg:grid-cols-[280px_1fr]">
    <ChatSettings
      :models="models"
      :models-loading="modelsLoading"
      :models-error="modelsError"
      :keys="keysStore.items"
      :keys-loading="keysStore.loading"
      v-model:selected-model="selectedModel"
      v-model:selected-key-id="selectedKeyId"
      v-model:temperature="temperature"
      v-model:max-tokens="maxTokens"
      v-model:system-prompt="systemPrompt"
      v-model:tool-calling-enabled="toolCallingEnabled"
      @clear="clearThread"
      @refresh-models="refreshModels"
    />

    <section class="flex flex-col min-h-0">
      <div class="flex-1 min-h-0">
        <ChatThread :messages="messages" :banner="banner" />
      </div>
      <ChatInput
        :disabled="streaming || activeKeys.length === 0 || !selectedModel"
        :streaming="streaming"
        @send="onSend"
        @stop="stopStreaming"
      />
    </section>
  </div>
</template>
